import ApiService from '@/services/api'
import {
  saveProject,
  saveStyle,
  saveOption,
  buildFilterQuery
} from '@/store/utils'
import { optionMetaKeys } from '@/constants/meta'

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const createOption = async ({ commit }, payload) => {
  const response = await ApiService.createOption(
    payload.styleId,
    payload.index,
    payload.file,
    payload.meta
  )
  const option = response.data

  commit('addOption', option)

  return option
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {object}       payload
 */
const deleteOption = async ({ commit }, payload) => {
  commit('deleteOption', payload.optionId)
  await ApiService.deleteOption(payload.styleId, payload.optionId)
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object[]}
 */
const fetchOptionsPaginated = async ({ commit, getters }, payload) => {
  const limit = payload.limit || getters.getOptionsPaginatedLimit
  const page = payload.page || getters.getOptionsPaginatedPage
  const filterGroups = {
    ...getters.getStyleMetaFilters,
    ...getters.getOptionMetaFilters
  }
  const filterQuery = buildFilterQuery(payload.filters, filterGroups, true)

  const response = await ApiService.fetchOptionsPaginated(
    payload.groupId,
    payload.seasonId,
    payload.projectId,
    page,
    limit,
    filterQuery,
    payload.searchQuery,
    payload.order
  )

  // error has already addressed by the APIService
  if (response.message || response.canceled) {
    return
  }

  const options = response.data.results

  options.forEach(option => {
    saveOption({ commit, getters }, option)
    saveProject({ commit, getters }, option.project)
    saveStyle({ commit, getters }, option.style)
  })

  if (response.data.next === null) {
    commit('setHasReachedOptionsPaginatedEnd', true)
  }

  return options
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 */
const incrementOptionsPaginatedPage = ({ commit }) => {
  commit('incrementOptionsPaginatedPage')
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 */
const resetOptionsPaginated = ({ commit }) => {
  commit('resetOptionsPaginated')
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateOption = async ({ commit }, payload) => {
  const response = await ApiService.updateOption(
    payload.styleId,
    payload.optionId,
    payload.index,
    payload.meta,
    payload.file
  )
  const option = response.data

  commit('updateOption', option)

  return option
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const updateOptionIndexStoreOnly = ({ commit, getters }, payload) => {
  const option = getters.getOptionById(payload.optionId)

  commit('updateOption', { ...option, index: payload.index })

  return option
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {boolean}      option
 */
const setOptionUploadStatus = ({ commit }, option) => {
  commit('setOptionUploadStatus', option)
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {string}       latestDeliveryAssigned
 */
const setLatestDeliveryAssigned = ({ commit }, latestDeliveryAssigned) => {
  commit('setLatestDeliveryAssigned', latestDeliveryAssigned)
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 *
 * @returns {object[]}
 */
const fetchOptionMetaFilters = async ({ commit }) => {
  const result = await Promise.all(
    Object.values(optionMetaKeys).map(key => ApiService.fetchMetaFilters(key))
  )

  const filters = {
    [optionMetaKeys.COLOR_ID]: {
      name: 'Color',
      values: result[0].data
    },
    [optionMetaKeys.DELIVERY_ID]: {
      name: 'Delivery',
      values: result[1].data
    },
    [optionMetaKeys.ASSORTMENT_ID]: {
      name: 'Delivery Assortment',
      values: result[2].data
    }
  }

  commit('setOptionMetaFilters', filters)

  return filters
}

/**
 * @type {object}
 */
const actions = {
  createOption,
  deleteOption,
  fetchOptionsPaginated,
  updateOption,
  updateOptionIndexStoreOnly,
  setOptionUploadStatus,
  setLatestDeliveryAssigned,
  incrementOptionsPaginatedPage,
  resetOptionsPaginated,
  fetchOptionMetaFilters
}

export default actions
